import {
  Box,
  Chip,
  useTheme,
} from '@mui/material'
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceDot,
} from 'recharts'
import type { POForecastData } from '@/types/po'

interface OverviewChartProps {
  data: POForecastData
  lineType: 'linear' | 'monotone' | 'step' | 'stepAfter'
}

interface ChartPoint {
  x: number
  name: string
  actualCumulative: number | null
  forecastCumulative: number | null
}

interface InvoiceDot {
  x: number
  y: number
  amount: number
  date: string
}

function clampMonth(value: number): number {
  return Math.min(Math.max(value, 1), 12)
}

export function buildStepChartData(data: POForecastData): ChartPoint[] {
  return data.monthlyData.map((month) => {
    // Keep the actual line continuous so months without bookings still render as plateaus.
    const actualCumulative = month.cumulativeActual

    // Keep forecast continuous from the forecast start onward, including months with booked invoices.
    const forecastStartsAt = data.currentMonth > 0 ? data.currentMonth : 1
    const forecastCumulative = data.isPastYear || month.month < forecastStartsAt
      ? null
      : month.cumulativeForecast

    return {
      x: month.month,
      name: month.monthName,
      actualCumulative,
      forecastCumulative,
    }
  })
}

export function buildInvoiceDots(data: POForecastData): InvoiceDot[] {
  return (data.invoiceMarkers || []).map((marker) => {
    const month = clampMonth(Math.floor(marker.monthPosition))
    const monthData = data.monthlyData[month - 1]

    return {
      x: marker.monthPosition,
      y: monthData?.cumulativeForecast ?? 0,
      amount: marker.amount,
      date: marker.date,
    }
  })
}

export function OverviewChart({ data, lineType }: OverviewChartProps) {
  const theme = useTheme()
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value)

  const chartData = buildStepChartData(data)
  const invoiceDots = buildInvoiceDots(data)

  const monthLabel = (value: number) => {
    const match = data.monthlyData.find((m) => m.month === value)
    return match ? match.monthName : String(value)
  }

  const maxInvoiceX = invoiceDots.length > 0
    ? Math.max(...invoiceDots.map((dot) => dot.x))
    : 12
  const maxX = Math.max(12, maxInvoiceX)
  const maxCumulative = data.monthlyData.length > 0
    ? Math.max(...data.monthlyData.map((month) => month.cumulativeForecast))
    : 0
  const maxY = Math.max(maxCumulative, data.totalPot, data.totalForecast, 1)

  const formatLabel = (label: number) => {
    if (!Number.isFinite(label)) return String(label)
    const month = clampMonth(Math.floor(label))
    const monthName = monthLabel(month)
    const fraction = label - month
    if (fraction <= 0) {
      return `1. ${monthName}`
    }
    const daysInMonth = new Date(Date.UTC(data.year, month, 0)).getUTCDate()
    const day = Math.min(Math.max(Math.round(fraction * daysInMonth + 1), 1), daysInMonth)
    return `${day}. ${monthName}`
  }

  const forecastLineColor = data.isExceeded ? theme.palette.error.main : theme.palette.primary.main

  return (
    <Box>
      <ResponsiveContainer width="100%" height={300}>
        <ComposedChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis
            dataKey="x"
            type="number"
            domain={[1, maxX]}
            ticks={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]}
            tickFormatter={monthLabel}
            stroke={theme.palette.text.secondary}
            tick={{ fill: theme.palette.text.secondary }}
          />
          <YAxis
            stroke={theme.palette.text.secondary}
            tick={{ fill: theme.palette.text.secondary }}
            tickFormatter={(value) => `€${(value / 1000).toFixed(0)}k`}
            domain={[0, Math.ceil(maxY * 1.05)]}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: theme.shape.borderRadius,
            }}
            labelFormatter={(label) => formatLabel(Number(label))}
            formatter={(value: number, name: string, props: { payload?: { amount?: number } }) => {
              if (name === 'Invoices' && props.payload?.amount != null) {
                return [formatCurrency(props.payload.amount), 'Invoice']
              }
              return [formatCurrency(Number(value)), name]
            }}
          />
          <Legend />
          <ReferenceLine
            y={data.totalPot}
            stroke={theme.palette.text.primary}
            strokeWidth={2}
            ifOverflow="extendDomain"
            label={{
              value: `Budget ${formatCurrency(data.totalPot)}`,
              fill: theme.palette.text.primary,
              position: 'insideTopRight',
            }}
          />
          <Line
            type={lineType}
            dataKey="actualCumulative"
            stroke={theme.palette.success.main}
            strokeWidth={2}
            dot={false}
            connectNulls={false}
            name="Actual Spend"
          />
          <Line
            type={lineType}
            dataKey="forecastCumulative"
            stroke={forecastLineColor}
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={false}
            connectNulls={false}
            name={data.isExceeded ? 'Forecast (Exceeded)' : 'Forecast'}
          />
          {invoiceDots.map((dot) => (
            <ReferenceDot
              key={`${dot.date}-${dot.amount}-${dot.x}`}
              x={dot.x}
              y={dot.y}
              r={6}
              fill={theme.palette.text.primary}
              stroke={theme.palette.background.paper}
              strokeWidth={2}
              ifOverflow="extendDomain"
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 2 }}>
        <Chip
          label={`Total PO Budget: €${data.totalPot.toLocaleString('de-DE')}`}
          color="primary"
          variant="outlined"
        />
        <Chip
          label={`Used: €${data.totalUsed.toLocaleString('de-DE')}`}
          color="success"
          variant="outlined"
        />
        {data.isExceeded ? (
          <Chip
            label={`Forecast Exceeded: €${(data.totalForecast - data.totalPot).toLocaleString('de-DE')}`}
            color="error"
          />
        ) : (
          <Chip
            label={`Remaining Forecast: €${(data.totalPot - data.totalForecast).toLocaleString('de-DE')}`}
            color="default"
            variant="outlined"
          />
        )}
      </Box>
    </Box>
  )
}
